import { Button, HelaService, HubCampaignsService, Input, LogService, Modal, ToastHelper } from '@hub/shared'
import ErrorMessage from '@src/enums/error-message.enum'
import { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import './modal-remove-interest.component.scoped.scss'
import './modal-remove-interest.component.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  openModal: boolean
  opportunity?: boolean
  campaignId?: string
  campaignName?: string
  idNetwork: string
  refuse: boolean
  onCloseModal?: (close: boolean) => any
  onCloseModalContract?: (close: boolean) => any
}

export default ({
  openModal,
  opportunity,
  campaignId = '',
  campaignName,
  idNetwork,
  refuse,
  onCloseModal,
  onCloseModalContract,
}: Props) => {
  const [t] = useTranslation('modalRemoveInterest')
  const toast = useMemo(() => new ToastHelper().toast, [])
  const logService = useMemo(() => new LogService(), [])
  const hubCampaignsService = useMemo(() => new HubCampaignsService(), [])
  const helaService = useMemo(() => new HelaService(), [])
  const [width, setWidth] = useState<number>(window?.innerWidth)
  const [height, setHeight] = useState<number>(window?.innerHeight)
  const [loading, setLoading] = useState<boolean>(false)
  const [reason, setReason] = useState<string>('')
  const navigate = useNavigate()

  useLayoutEffect(() => {
    const updateWidth = () => {
      setWidth(window?.innerWidth)
    }
    const updateHeight = () => {
      setHeight(window?.innerHeight)
    }
    window.addEventListener('resize', updateWidth)
    window.addEventListener('resize', updateHeight)
    return () => {
      window.removeEventListener('resize', updateWidth)
      window.removeEventListener('resize', updateHeight)
    }
  }, [])

  const handleCloseModal = useCallback(
    (value: boolean) => {
      if (onCloseModal && typeof onCloseModal === 'function') {
        onCloseModal(value)
      }
    },
    [onCloseModal]
  )

  const handleCloseModalContract = useCallback(
    (value: boolean) => {
      if (onCloseModalContract && typeof onCloseModalContract === 'function') {
        onCloseModalContract(value)
      }
    },
    [onCloseModalContract]
  )

  const removeInterest = useCallback(async () => {
    setLoading(true)
    hubCampaignsService
      .postCampaignsAction(campaignId, idNetwork, 'refused', reason, opportunity)
      .then((e) => {
        if (!!e) {
          handleCloseModal(true)
          toast.success(t('success'), {
            closeButton: true,
            className: 'full text-bold',
            position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
            duration: 10000,
            notOverClick: true,
            immediately: true,
          })
          navigate('/campaigns')
        }
      })
      .catch((err) => {
        logService.error({ message: err?.data?.errorCode ?? t('errors.defaultError') })
        const messageError = err?.data?.errorCode ? t(`errors.${(ErrorMessage as any)[err?.data?.errorCode]}`) : t('errors.defaultError')
        toast.error(messageError, {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 10000,
          notOverClick: true,
          immediately: true,
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }, [hubCampaignsService, campaignId, idNetwork, reason, opportunity, handleCloseModal, toast, t, logService, navigate])

  const handleRefuseContract = useCallback(async () => {
    setLoading(true)
    helaService
      .patchRecruitmentContract(campaignId, [idNetwork], 'influencerRefused', true, reason)
      .then((e) => {
        if (!!e) {
          handleCloseModal(true)
          handleCloseModalContract(true)
          toast.success(t('refuse.success'), {
            closeButton: true,
            className: 'full text-bold',
            position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
            duration: 10000,
            notOverClick: true,
            immediately: true,
          })
          navigate('/campaigns')
        }
      })
      .catch((err) => {
        logService.error({ message: err?.data?.errorCode ?? t('errors.defaultError') })
        const messageError = err?.data?.errorCode ? t(`errors.${(ErrorMessage as any)[err?.data?.errorCode]}`) : t('errors.defaultError')
        toast.error(messageError, {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 10000,
          notOverClick: true,
          immediately: true,
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }, [helaService, campaignId, idNetwork, reason, handleCloseModal, handleCloseModalContract, toast, t, logService, navigate])

  return (
    <Modal
      className={`modal-remove-interest ${refuse ? 'refuse' : ''}`}
      open={openModal}
      onCloseChange={() => handleCloseModal(true)}
      buttonClose={!loading}
      modalSize={refuse && width < 991 ? 'lg' : refuse ? 'md' : 'sm'}
      header={refuse ? null : <h5 className="mb-0">{t('title')}</h5>}
      footer={
        <div className="display-flex justify-content-space-between" style={{ width: '100%' }}>
          <Button
            id="modal-acceptance-contract-refuse"
            onClick={() => handleCloseModal(true)}
            size="sm"
            color="transparent"
            textColor="var(--gray)"
            borderColor="var(--gray)"
            disabled={loading}
          >
            <span>{t('cancel')}</span>
          </Button>
          <Button
            id="modal-acceptance-contract-accept"
            onClick={() => (refuse ? handleRefuseContract() : removeInterest())}
            loading={loading}
            size="sm"
            disabled={refuse && reason?.length < 5}
          >
            <span>{t('confirm')}</span>
          </Button>
        </div>
      }
    >
      {refuse ? (
        <div className="content" style={width < 991 ? { height: `calc(${height}px - 30px - 56px - 61px)` } : { gap: '2rem' }}>
          <i className="fa-solid fa-ban" style={{ color: 'var(--red)', fontSize: '12rem' }}></i>
          <div className="text">
            <h1>{t('refuse.title')}</h1>
            <p dangerouslySetInnerHTML={{ __html: t('refuse.message', { campaignName: campaignName }) }}></p>
          </div>
          <div>
            <hr />
            <Input
              name="input-content-link"
              id="input-content-link"
              label={t('refuse.inputLabel')}
              value={reason}
              onChange={(value) => setReason(value)}
              readOnly={loading}
              type="textarea"
            />
          </div>
        </div>
      ) : (
        <p className="my-3">{t('message')}</p>
      )}
    </Modal>
  )
}
